import {
  ApiError,
  deleteRequest,
  getRequest,
  PaginatedResponse,
  patchRequest,
  postRequest,
  putRequest
} from 'features/common'
import { createApiError } from 'features/common/api/apiError'
import { formatFqnForBigQuery } from 'features/common/queries/bigQueryQueries'
import { getClient } from 'features/common/utils/clientParameter'
import { encodeUriParams } from 'features/common/utils/searchParamsUtils'
import { Map } from 'features/maps/types'
import { sendSQLPostRequest } from 'features/sql/api/sql'
import { User } from 'features/users'
import settings from 'settings'
import { OAuthConsentUrl, OAuthState, ProviderType } from '..'
import { OAuthRedirectUri, REDIRECT_URI } from '../constants'
import {
  AttachConnectionCredentialsParams,
  BigQueryConnectionDataset,
  BigQueryConnectionProject,
  Connection,
  ConnectionPrivacy,
  CredentialsMutationResponse,
  Dataset,
  OAuthBQState,
  OauthConsentRequest,
  OAuthSnowflakeState,
  OAuthDatabricksState,
  PatchConnectionParams
} from '../types'

function baseUrl() {
  const { workspaceApiUrl } = settings
  return `${workspaceApiUrl}/connections`
}

export const getConnections = async (): Promise<Array<Connection>> => {
  const connections = await getRequest(baseUrl())
  return connections.map((c) => ({ ...c, config: c.config || {} }))
}

export const getConnection = async (id: string): Promise<Connection | null> => {
  if (!id) {
    return null
  }

  const connection = await getRequest(`${baseUrl()}/${id}`)
  return { ...connection, config: connection.config || {} }
}

export const attachConnectionCredentials = async ({
  connectionId,
  config
}: AttachConnectionCredentialsParams): Promise<Connection> => {
  const connection = await putRequest(`${baseUrl()}/${connectionId}/attach-credentials`, config)
  return connection
}

export const attachMultipleConnectionsCredentials = async (connections: AttachConnectionCredentialsParams[]) => {
  return await Promise.all(
    connections.map(async ({ config, connectionId }) => {
      try {
        await attachConnectionCredentials({ connectionId, config })
        return {
          connectionId
        }
      } catch (error) {
        return {
          connectionId,
          error: createApiError(error)
        }
      }
    })
  )
}

export const saveUserOwnerConnections = async (
  connections: Connection[]
): Promise<{ connectionId: string; error?: ApiError }[]> => {
  return await Promise.all(
    connections.map(async (connection) => {
      try {
        await saveConnection(connection)
        return { connectionId: connection.id }
      } catch (error) {
        return {
          connectionId: connection.id,
          error: createApiError(error)
        }
      }
    })
  )
}

export const removeConnectionCredentials = async ({
  connectionId
}: {
  connectionId: string
}): Promise<{ status: string }> => {
  const connection = await deleteRequest(`${baseUrl()}/${connectionId}/attach-credentials`)
  return connection
}

export const removeMultipleConnectionCredentials = async (
  connections: { connectionId: string }[]
): Promise<CredentialsMutationResponse[]> => {
  return await Promise.all(
    connections.map(async ({ connectionId }) => {
      try {
        await removeConnectionCredentials({ connectionId })
        return {
          connectionId
        }
      } catch (error) {
        return {
          connectionId,
          error: createApiError(error)
        }
      }
    })
  )
}

export const saveConnection = (conn: Connection): Promise<Connection> => {
  const newConn = { ...conn }
  newConn.spatial_extension = undefined

  if (!newConn.id) {
    return createConnection(newConn)
  } else {
    return patchConnection(newConn)
  }
}

const createConnection = async (conn: Connection): Promise<Connection> => {
  const payload = cleanUpPayload(conn)
  const connection = await postRequest(baseUrl(), payload)
  return connection
}

export const patchConnection = async (conn: PatchConnectionParams): Promise<Connection> => {
  const connection: Connection = await patchRequest(`${baseUrl()}/${conn.id}`, {
    spatial_extension: conn.spatial_extension,
    config: conn.config,
    privacy: conn.privacy,
    groupIds: conn.privacy === ConnectionPrivacy.Shared ? conn.groups?.map((group) => group.id) || [] : undefined,
    tempLocations: conn.tempLocations,
    workspaceLocations: conn.workspaceLocations,
    extensionsLocations: conn.extensionsLocations,
    doLocation: conn.doLocation,
    atLocation: conn.atLocation,
    viewerCredentials: conn.viewerCredentials,
    maxConcurrentQueries: conn.maxConcurrentQueries,
    queryTimeoutS: conn.queryTimeoutS,
    sharingScope: conn.sharingScope,
    namedSourcesOnly: conn.namedSourcesOnly
  })

  // FIXME: It's a temporary fix. It will be fixed in Backend ASAP
  // eslint-disable-next-line no-prototype-builtins
  if (conn.hasOwnProperty('viewerCredentials')) {
    connection.viewerCredentialsAttached = conn.viewerCredentials
  }
  // -----

  return connection
}

export const deleteConnection = async (id: string): Promise<void> => {
  await deleteRequest(`${baseUrl()}/${id}`)
}

export const deleteConnectionWithTransfer = async (id: string, targetConnectionId: string): Promise<void> => {
  await deleteRequest(`${baseUrl()}/${id}`, {
    body: JSON.stringify({ targetConnectionId }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

export const getBigQueryConnectionProjects = async (
  id: string,
  search?: string
): Promise<Array<BigQueryConnectionProject>> => {
  const { workspaceApiUrl } = settings
  const connections = await getRequest(
    `${workspaceApiUrl}/connection/bigquery/${id}/projects${search ? `?search=${search}` : ''}`
  )
  return connections
}

export const getOAuthConstentUrl = async ({ provider, params }: OauthConsentRequest): Promise<OAuthConsentUrl> => {
  switch (provider) {
    case ProviderType.BIGQUERY:
      return {
        ...(await getBQOAuthConsentUrl()),
        provider
      }
    case ProviderType.SNOWFLAKE:
      return {
        ...(await getSnowflakeOAuthConsentUrl(params)),
        provider
      }
    case ProviderType.DATABRICKS_REST:
      return {
        ...(await getDatabricksOAuthConsentUrl()),
        provider
      }
    case ProviderType.DATABRICKS_WAREHOUSE:
      return {
        ...(await getDatabricksWarehouseOAuthConsentUrl()),
        provider
      }
    default:
      throw new Error('Provider not supported')
  }
}

const getBQOAuthConsentUrl = async (): Promise<{ url: string }> => {
  const redirectUri = new URL(`/${OAuthRedirectUri[ProviderType.BIGQUERY]}`, location.href)
  const consentURL: { url: string } = await getRequest(
    `${baseUrl()}/oauth/bigquery/consent-url?${REDIRECT_URI}=${redirectUri}`
  )
  return consentURL
}

export const getSnowflakeRedirectUri = () => {
  return new URL(`/${OAuthRedirectUri[ProviderType.SNOWFLAKE]}`, location.href)
}

const getSnowflakeOAuthConsentUrl = async (params = {}): Promise<{ url: string }> => {
  const redirectUri = getSnowflakeRedirectUri()
  const filteredParams: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      filteredParams[key] = String(value)
    }
  }
  const searchParams = new URLSearchParams(filteredParams).toString()
  const consentURL: { url: string } = await getRequest(
    `${baseUrl()}/oauth/snowflake/consent-url?${REDIRECT_URI}=${redirectUri}&${searchParams}`
  )
  return consentURL
}

const getDatabricksOAuthConsentUrl = async (): Promise<{ url: string }> => {
  const redirectUri = new URL(`/${OAuthRedirectUri[ProviderType.DATABRICKS_REST]}`, location.href)
  const consentURL: { url: string } = await getRequest(
    `${baseUrl()}/oauth/databricks/consent-url?${REDIRECT_URI}=${redirectUri}`
  )
  return consentURL
}

const getDatabricksWarehouseOAuthConsentUrl = async (): Promise<{ url: string }> => {
  const redirectUri = new URL(`/${OAuthRedirectUri[ProviderType.DATABRICKS_WAREHOUSE]}`, location.href)
  const consentURL: { url: string } = await getRequest(
    `${baseUrl()}/oauth/databricks/consent-url?${REDIRECT_URI}=${redirectUri}`
  )
  return consentURL
}

export const getOAuthState = async ({
  provider,
  params
}: {
  provider: ProviderType
  params: string
}): Promise<OAuthState> => {
  switch (provider) {
    case ProviderType.BIGQUERY:
      return getBQOAuthState(params)
    case ProviderType.SNOWFLAKE:
      return getSnowflakeOAuthState(params)
    case ProviderType.DATABRICKS_REST:
    case ProviderType.DATABRICKS_WAREHOUSE:
      return getDatabricksOAuthState(params)
    default:
      throw new Error('Provider not supported')
  }
}

export const getBQOAuthState = async (params: string): Promise<OAuthBQState> => {
  return await getRequest(`${baseUrl()}/oauth/bigquery/callback${params}`)
}

export const getSnowflakeOAuthState = async (params: string): Promise<OAuthSnowflakeState> => {
  return await getRequest(`${baseUrl()}/oauth/snowflake/callback${params}`)
}

export const getDatabricksOAuthState = async (params: string): Promise<OAuthDatabricksState> => {
  return await getRequest(`${baseUrl()}/oauth/databricks/callback${params}`)
}

export const getOAuthBillingProjectsRequest = async ({
  state,
  client_email,
  search
}: {
  state: string
  client_email: string
  search?: string
}): Promise<Array<BigQueryConnectionProject>> => {
  const { workspaceApiUrl } = settings
  const connections = await postRequest(`${workspaceApiUrl}/connection/bigquery/projects`, {
    state,
    client_email,
    search
  })
  return connections
}

export const getConnectionRelatedMaps = async (id: string): Promise<PaginatedResponse<Map>> => {
  const { workspaceApiUrl } = settings
  const connections = await getRequest(`${workspaceApiUrl}/connections/${id}/maps`)
  return connections
}

export const getConnectionRelatedWorkflows = async (id: string): Promise<{ id: string; title: string }[]> => {
  const { workspaceApiUrl } = settings
  const connections = await getRequest(`${workspaceApiUrl}/connections/${id}/workflows`)
  return connections
}

export const getServiceAccountProjects = async (serviceAccount: string): Promise<Array<BigQueryConnectionProject>> => {
  const { workspaceApiUrl } = settings
  const connections = await postRequest(`${workspaceApiUrl}/connection/bigquery/projects`, JSON.parse(serviceAccount))
  return connections
}

export const getBqProjects = async (payload): Promise<Array<BigQueryConnectionProject>> => {
  const { workspaceApiUrl } = settings
  const connections = await postRequest(`${workspaceApiUrl}/connection/bigquery/projects`, payload)
  return connections
}

export const getBigQueryConnectionDatasets = async ({
  connectionId,
  projectId
}: {
  connectionId: string
  projectId: string
}): Promise<Array<BigQueryConnectionDataset>> => {
  const { workspaceApiUrl } = settings
  const connections = await getRequest(
    `${workspaceApiUrl}/connection/bigquery/${connectionId}/projects/${projectId}/datasets`
  )
  return connections
}

const cleanUpPayload = (connection: Connection) => {
  return {
    name: connection?.name,
    provider_id: connection?.provider_id,
    mapcount: connection?.mapcount,
    config: connection?.config,
    shared: connection?.shared,
    updated_at: connection?.updated_at,
    ownerPicture: connection?.ownerPicture,
    ownerEmail: connection?.ownerEmail,
    carto_dw: connection?.carto_dw,
    spatial_extension: connection?.spatial_extension,
    privacy: connection?.privacy,
    groupIds:
      connection?.privacy === ConnectionPrivacy.Shared ? connection.groups?.map((group) => group.id) || [] : undefined,
    tempLocations: connection?.tempLocations,
    workspaceLocations: connection?.workspaceLocations,
    extensionsLocations: connection?.extensionsLocations,
    doLocation: connection?.doLocation,
    atLocation: connection?.atLocation,
    viewerCredentials: connection?.viewerCredentials,
    external_idp_id: connection?.external_idp_id,
    maxConcurrentQueries: connection?.maxConcurrentQueries,
    queryTimeoutS: connection?.queryTimeoutS,
    sharingScope: connection?.sharingScope,
    namedSourcesOnly: connection?.namedSourcesOnly
  }
}

export const trackConnectionProviderSelectedRequest = async ({
  user,
  providerId
}: {
  user: User
  providerId: string
}): Promise<void> => {
  const { workspaceApiUrl } = settings

  const metricsEndpointUrl = `${workspaceApiUrl}/events`
  await postRequest(metricsEndpointUrl, {
    event_type: 'ConnectionProviderSelected',
    payload: {
      accountId: user.user_metadata.account_id,
      userId: user.user_id,
      providerId: providerId
    },
    timestamp: new Date()
  })
}

export const versionFromSqlQuery = async (connection: Connection, atLocation?: string) => {
  if (
    ![ProviderType.SNOWFLAKE, ProviderType.REDSHIFT, ProviderType.POSTGRES, ProviderType.BIGQUERY].includes(
      connection.provider_id
    )
  ) {
    throw new Error('Only BigQuery, Snowflake, Redshift and Postgres supports carto.VERSION_ADVANCED() function')
  }
  if (!atLocation) {
    throw new Error('database not defined and no default database in connection')
  }

  let query = `SELECT ${atLocation}.VERSION_ADVANCED() AS "version_advanced"`
  if (ProviderType.BIGQUERY === connection.provider_id) {
    query = `SELECT ${atLocation}.VERSION_ADVANCED() AS version_advanced`
  }

  const response = await sendSQLPostRequest({
    connection: connection.name,
    query,
    extraParams: { client: getClient() }
  })

  const result = response?.rows?.length ? response.rows[0] : {}
  const version = result?.version_advanced || ''

  return version
}

export const getRecentResources = async (max?: number): Promise<Array<Dataset>> => {
  const params = max ? `?pageSize=${max}` : ''
  const recentResourcesEndpointUrl = `${baseUrl()}/resources/recent${params}`
  const resources = await getRequest(recentResourcesEndpointUrl)
  return resources
}

export const getAnalyticsToolboxLocation = async ({
  connectionId,
  region
}: {
  connectionId: string
  region?: string
}): Promise<string> => {
  const regionStr = region ? `?region=${region}` : ''
  const response = await getRequest(`${baseUrl()}/${connectionId}/analyticsToolboxLocation${regionStr}`)
  const isBQ = response?.provider === ProviderType.BIGQUERY
  return (
    isBQ ? formatFqnForBigQuery(response?.analyticsToolboxLocation) : response?.analyticsToolboxLocation
  ) as string
}

export const getDatabricksMetadata = async (payload: {
  host: string
  token?: string
  servicePrincipalClientId?: string
  servicePrincipalSecret?: string
  userIdentifier?: string
  userEmail?: string
  state?: string
  includeCatalogs: boolean
  includeWarehouses: boolean
  includeClusters: boolean
}) => {
  const { workspaceApiUrl } = settings
  return await postRequest(`${workspaceApiUrl}/connection/databricks/metadata`, payload)
}

export const getExistingDatabricksMetadata = async (
  id: string,
  payload: { includeCatalogs: boolean; includeWarehouses: boolean; includeClusters: boolean }
) => {
  const { workspaceApiUrl } = settings
  return await getRequest(`${workspaceApiUrl}/connection/databricks/${id}/metadata?${encodeUriParams(payload)}`)
}

export const getDatabricksWarehouseMetadata = async (payload: {
  host: string
  token?: string
  servicePrincipalClientId?: string
  servicePrincipalSecret?: string
  userIdentifier?: string
  userEmail?: string
  state?: string
  includeCatalogs: boolean
  includeWarehouses: boolean
  includeClusters: boolean
}) => {
  const { workspaceApiUrl } = settings
  return await postRequest(`${workspaceApiUrl}/connection/databricks/metadata`, payload)
}

export const getExistingDatabricksWarehouseMetadata = async (
  id: string,
  payload: { includeCatalogs: boolean; includeWarehouses: boolean; includeClusters: boolean }
) => {
  const { workspaceApiUrl } = settings
  return await getRequest(`${workspaceApiUrl}/connection/databricks/${id}/metadata?${encodeUriParams(payload)}`)
}
